import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {bool, object} from "prop-types";
import Title from "features/components/title";
import Text from "features/components/text";
import {__} from "services/i18n";
import {get} from "lodash";
import {COMPANY_ROLES} from "constants.js";
import Button from "features/components/button";
import {connect} from "react-redux";
import {showDisassociateModal} from "features/modals/company/disassociate/reducer";
import {editCompany, startDeleteCompany, startResetCompanyCode} from "store/reducer/company/reducer";
import {CompanyValidator} from "services/utils/validations";
import {Icon} from "features/components/icon";
import {TextField} from "features/components/text-field";
import {withToastManager} from "react-toast-notifications";
import {
    companyCodeChangeError,
    companyCodeChangeLoading,
    companyDeleteError,
    companyDeleteLoading, companyDeleteSuccess
} from "store/reducer/company/selectors";
import {showDisassociateUserModal} from "features/modals/company/disassociate-user/reducer";
import {generateFullUrl} from "services/utils/utils";
import {ADMIN_ROLE_KEY} from "constants.js";
import {ModalPortal} from "features/components/modal";

/* istanbul ignore next */
const mapStateToProps = state => ({
    code_loading: companyCodeChangeLoading(state),
    code_error: companyCodeChangeError(state),
    delete_loading: companyDeleteLoading(state),
    delete_error: companyDeleteError(state),
    delete_success: companyDeleteSuccess(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    showDisassociateModal,
    showDisassociateUserModal,
    editCompany,
    startResetCompanyCode,
    startDeleteCompany
};

export const CompanyInfo = (props) => {
    const {company, showDisassociateModal, editCompany, showValid, extended,
        startResetCompanyCode, code_loading, code_error, showDisassociateUserModal,
        delete_loading, delete_error, startDeleteCompany, delete_success} = props;

    const onDelete = useCallback(() => {
        showDisassociateModal(company.id);
    }, [showDisassociateModal, company]);

    const editCompanyClick = useCallback(() => {
        editCompany(company);
    }, [editCompany, company]);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const deleteCompanyClick = useCallback(() => {
        setShowDeleteModal(true);
    }, [setShowDeleteModal]);
    React.useEffect(() => {
        if(delete_success) {
            setShowDeleteModal(false);
        }
    }, [delete_success]);


    const doDeleteCompany = useCallback(() => {
        startDeleteCompany({id: company.id})
    }, [startDeleteCompany, company]);

    const isCompanyValid = useMemo(() => {
        return CompanyValidator(company);
    }, [company]);

    const imageSource = useMemo(() => {
        if (!company.logo) {
            return null
        }
        return company.logo.src || generateFullUrl(company.logo);
    }, [company]);

    const isExtended = useMemo(() => {
        return !!extended && company.role === ADMIN_ROLE_KEY
    }, [company, extended]);

    const { toastManager } = props;
    const textareaRef = React.useRef(null);

    /*istanbul ignore next*/
    const copyCode = useCallback(async () => {
        textareaRef.current.select();
        //document.execCommand('copy');
        toastManager.add(__("toasts.copied"), { appearance: 'success', id: 'company-info-code-copy' })
    }, [toastManager, textareaRef]);
    const updateCode = useCallback(() => {
        startResetCompanyCode({id: company.id});
    }, [startResetCompanyCode, company]);

    React.useEffect(() => {
        if(code_error.inError) {
            toastManager.add(__(code_error.error), { appearance: 'error', id: 'company-info-code-change-error'});
        }
    }, [code_error, toastManager])


    const companyAssociates = useMemo(() => {
        return company.associates || [];
    }, [company]);

    const disassociateUser = useCallback((user) => {
        showDisassociateUserModal({company: company.id, user});
    }, [showDisassociateUserModal, company]);

    const [showRequestConnectModal, setShowRequestConnectModal] = useState(false);

    const requestConnectModal = useCallback(() => {
        if(!showRequestConnectModal) return null;
        return <ModalPortal size={"small"} className={"request-connect-modal"}>
            <Title size={24}>{__("company-info.request.title")}</Title>
            <Text size={16}>{__("company-info.request.text")}</Text>
            <Button title={__("buttons.close")} onClick={() => setShowRequestConnectModal(false)} icon={"arrow-right"} />
        </ModalPortal>
    }, [showRequestConnectModal])

    return <div className="company-info-box">
        <div className={`company-info`}>
            <div className="content">
                <div className={`image ${!!imageSource ? "has-image" : ""}`}>
                    {!!imageSource && <img src={imageSource} alt={"logo"}/>}
                </div>
                <div className="name-role">
                    <Title size={20}>{company.name}</Title>
                    <Text size={16}>{__("common.role-of")} {__(get(COMPANY_ROLES.find(i => i.key === company.role), 'value', company.role))}</Text>
                </div>
            </div>
            <div className="buttons">
                {
                    company.role !== ADMIN_ROLE_KEY ?
                        <Button title={""} type={"square"} onClick={onDelete} icon={"delete"} /> :
                        <>
                            <Button
                                title={__(`${isCompanyValid ? "buttons.edit" : "buttons.check-and-complete"}`)}
                                type={isCompanyValid ? "outlined" : "default-type"}
                                onClick={editCompanyClick} />
                            <Button
                                title={__("buttons.delete")}
                                type={"outlined"}
                                status={"danger"}
                                onClick={deleteCompanyClick} />
                            {!!showValid && CompanyValidator(company) && <div className="valid">
                                <Icon>check-circle-outline</Icon>
                            </div>}
                        </>
                }
            </div>
        </div>
        {isExtended && <div className="company-info-extended">
            <div className="users">
                <Title size={18}>{__("company-info.request.title")}</Title>
                <Button title={__("company-info.request.button")} onClick={() => setShowRequestConnectModal(true)} type={"outlined"} />
                <Title size={18}>{__("company-info.users.title", [{key: "[companyName]", value: company.name}])}</Title>
                <div className="list">
                    {
                        companyAssociates.map(item => <div className="user-item" key={item.id}>
                            <div className="image">
                                {!!item.image && <img src={item.image} alt={"associate profile"} />}
                                {!item.image && <Icon>account-circle</Icon>}
                            </div>
                            <div className="name-role">
                                <Title size={16}>{item.firstName} {item.lastName}</Title>
                                <Text size={14}>{__(get(COMPANY_ROLES.find(i => i.key === item.role), 'value', item.role))}</Text>
                            </div>
                            <div className="remove-icon" onClick={() => disassociateUser(item)}><Icon>clear-circle-outlined</Icon></div>
                        </div>)
                    }
                    {companyAssociates.length === 0 && <Text size={16}>{__("company-info.users.no-users")}</Text>}
                </div>
            </div>
            <div className="code">
                <Text size={16}>{__("company-info.code.text")}</Text>
                <TextField type={"text"} value={company.secretCode || "00000"} onChange={() => {}} placeholder={__("company-info.code.placeholder")} disabled={true} />
                <div className="buttons">
                    <Button title={__("buttons.update-code")} status={code_loading ? "loading": "default-status"} onClick={updateCode} type={"clear"} size={"small"} />
                    <Button title={__("buttons.copy-code")} onClick={copyCode} type={"outlined"} size={"small"} />
                    <textarea readOnly={true} value={company.secretCode} ref={textareaRef} />
                </div>
            </div>
        </div>}
        {showDeleteModal && <ModalPortal className={"delete-company-modal"}>
            <Title size={24}>{__("company-info.delete.title")}</Title>
            <Text size={16}>{__("company-info.delete.text")}</Text>
            {delete_error.inError && <Text size={16} color={"danger"}>{__(delete_error.error)}</Text>}
            <div className={"buttons"}>
                <Button disabled={delete_loading} title={__("buttons.cancel")} onClick={() => setShowDeleteModal(false)} type={"outlined"} />
                <Button disabled={delete_loading} title={__("buttons.delete")} onClick={doDeleteCompany} status={delete_loading ? "loading" : "danger"} />
            </div>
        </ModalPortal>}
        {requestConnectModal()}
    </div>
};
CompanyInfo.propTypes = {
    company: object.isRequired,
    showValid: bool,
    extended: bool
};
export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(CompanyInfo));